import React, { useEffect, useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Button,
  Chip,
  Grid,
  Paper,
  // Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Avatar,
  IconButton,
  Breadcrumbs,
  Link,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TableContainer,
  TableCell,
  Table,
  TableHead,
  TableRow,
  TableBody,
  Tabs,
  Tab,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Person as PersonIcon,
  Schedule as ScheduleIcon,
  Description as DescriptionIcon,
  Source as SourceIcon,
  Business as BusinessIcon,
  Assignment as AssignmentIcon,
  Attachment as AttachmentIcon,
  Visibility as VisibilityIcon,
  Download as DownloadIcon,
  CalendarToday as CalendarIcon,
  AccessTime as TimeIcon,
  Language as LanguageIcon,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import PageLayout from '../../components/layout/PageLayout';
import {
  useGetLeadByIdQuery,
  useLazyGetLeadsStatusQuery,
  useLazyGetUserByMultiRolesQuery,
  useDownloadLeadFileMutation,
  useUpdateLeadStatusMutation,
} from '../../store/api';
import { toast } from 'react-toastify';
import { MEETING_EDIT_PATH, MEETING_VIEW_PATH } from '../../navigations/path';
import { extractList, extractData } from '../../utils/response';
import StatusDialog from '../../components/Leads/dialogs/StatusDialog';
import Can from '../../components/permissions/Can';
import { useLazySelectOptions } from '../../hooks/useLazySelectOptions';

const LeadDetail = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [openImageDialog, setOpenImageDialog] = useState(false);
  const [selectedImage, setSelectedImage] = useState('');
  const [meetingTab, setMeetingTab] = useState(0);
  const [downloadingAttachmentId, setDownloadingAttachmentId] = useState(null);
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [newStatus, setNewStatus] = useState('');
  const [statusProcessing, setStatusProcessing] = useState(false);
  const [conditionalData, setConditionalData] = useState({
    date: '',
    description: '',
    assignees: [],
    timeZone: 'UTC',
    startDateTime: '',
    endDateTime: '',
    meetingDescription: '',
  });

  const {
    data: leadResponse,
    isLoading,
    isError: leadError,
    refetch: refetchLead,
  } = useGetLeadByIdQuery(id, { skip: !id });

  const [fetchStatuses] = useLazyGetLeadsStatusQuery();
  const [fetchMultiRoleUsers] = useLazyGetUserByMultiRolesQuery();

  const statusSelect = useLazySelectOptions(fetchStatuses, {
    args: { 'conditions[model]': 'Lead', paginate: 0 },
    mapResult: (res) =>
      res?.data?.list ?? res?.data?.data?.list ?? extractList(res),
  });

  const frontSellerSelect = useLazySelectOptions(fetchMultiRoleUsers, {
    args: { 'roles[]': ['Sales', 'Lead Gen'], paginate: 0 },
    mapResult: (res) =>
      extractList(res?.data ?? res).map((u) => ({
        id: u?.uuid ?? u?.id,
        name: u?.name,
      })),
  });

  const [downloadLeadFile] = useDownloadLeadFileMutation();
  const [updateLeadStatus] = useUpdateLeadStatusMutation();

  const lead = extractData(leadResponse);
  const statusOptions = statusSelect.options;
  const frontSellerOptions = frontSellerSelect.options;

  useEffect(() => {
    if (statusDialogOpen) {
      statusSelect.load().catch(() => {
        toast.error('Failed to load status options');
      });
      frontSellerSelect.load();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional dependency list
  }, [statusDialogOpen, statusSelect.load, frontSellerSelect.load]);

  useEffect(() => {
    setMeetingTab(0);
  }, [id]);

  useEffect(() => {
    if (leadError) {
      toast.error('Error loading lead details');
      navigate('/leads');
    }
  }, [leadError, navigate]);

  useEffect(() => {
    if (!isLoading && leadResponse != null && !lead?.id) {
      toast.error('Failed to fetch lead details');
      navigate('/leads');
    }
  }, [isLoading, leadResponse, lead, navigate]);

  const handleBack = () => {
    navigate('/leads');
  };

  const handleImageClick = (imageUrl) => {
    setSelectedImage(imageUrl);
    setOpenImageDialog(true);
  };

  const handleDownloadFile = async (file) => {
    const filePath = file?.path;
    const fileName = file?.title || file?.file_name || file?.name || 'download';
    if (!filePath) {
      toast.error('No file path available for download');
      return;
    }
    try {
      setDownloadingAttachmentId(file?.id ?? filePath);
      const blobData = await downloadLeadFile(filePath).unwrap();
      const blob =
        blobData instanceof Blob
          ? blobData
          : new Blob([blobData], { type: 'application/octet-stream' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      toast.success(`${fileName} downloaded`);
    } catch (error) {
      toast.error(error?.message || 'Failed to download file');
    } finally {
      setDownloadingAttachmentId(null);
    }
  };

  const toDatetimeLocalValue = (value) => {
    if (value == null || value === '') return '';
    const d = new Date(String(value).replace(' ', 'T'));
    if (Number.isNaN(d.getTime())) return '';
    const pad = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  };

  const resolveLeadStatusId = (item) => {
    if (!item) return '';
    const raw =
      item.last_status_log?.status?.id ??
      item.status?.status?.id ??
      item.status?.id ??
      item.status_id;
    if (raw === undefined || raw === null || raw === '') return '';
    return raw;
  };

  const matchStatusId = (list, rawId) => {
    if (rawId === '' || rawId == null) return '';
    const found = (list || []).find(
      (s) => s.id === rawId || String(s.id) === String(rawId)
    );
    return found ? found.id : rawId;
  };

  const handleOpenStatusDialog = async () => {
    if (!lead) return;
    try {
      const opts = await statusSelect.load();
      frontSellerSelect.load();
      const rawStatusId = resolveLeadStatusId(lead);
      const statusId = matchStatusId(opts, rawStatusId);
      const log = lead?.last_status_log;
      const meeting = log?.meeting;
      const statusName = log?.status?.name || lead?.status?.status?.name || lead?.status?.name;
      const assigneeIdsFromLead =
        lead?.assignees?.map((a) => a.id ?? a.uuid).filter(Boolean) ?? [];

      const nextConditionalData = {
        date: '',
        description: '',
        assignees: [],
        timeZone: 'UTC',
        startDateTime: '',
        endDateTime: '',
        meetingDescription: '',
      };

      if (statusName === 'Meeting' && meeting) {
        nextConditionalData.assignees = assigneeIdsFromLead;
        nextConditionalData.timeZone = meeting.time_zone || 'UTC';
        nextConditionalData.startDateTime = toDatetimeLocalValue(meeting.start_date_time);
        nextConditionalData.endDateTime = toDatetimeLocalValue(meeting.end_date_time);
        nextConditionalData.meetingDescription = meeting.description || '';
      } else if (log && statusName && statusName !== 'Meeting') {
        nextConditionalData.date = log.date || log.created_at?.slice?.(0, 10) || '';
        nextConditionalData.description = log.description || '';
      }

      setNewStatus(statusId);
      setConditionalData(nextConditionalData);
      setStatusDialogOpen(true);
    } catch {
      toast.error('Failed to load status options');
    }
  };

  const handleCloseStatusDialog = () => {
    setStatusDialogOpen(false);
    setNewStatus('');
    setConditionalData({
      date: '',
      description: '',
      assignees: [],
      timeZone: 'UTC',
      startDateTime: '',
      endDateTime: '',
      meetingDescription: '',
    });
  };

  const handleConditionalChange = (field, value) => {
    setConditionalData((prev) => ({ ...prev, [field]: value }));
  };

  const handleStatusChange = async () => {
    if (!lead?.id) return;
    if (!newStatus) {
      toast.warn('Select a status');
      return;
    }

    const statusObj = statusOptions.find((s) => s.id === newStatus);
    const statusName = statusObj?.name;
    const payload = {
      status_id: newStatus,
      date: conditionalData.date,
      description: conditionalData.description,
    };

    if (statusName === 'Select') {
      if (!conditionalData.date || !conditionalData.description) {
        toast.warn('Date & description are required');
        return;
      }
    }

    if (statusName === 'Meeting') {
      if (
        !conditionalData.assignees?.length ||
        !conditionalData.startDateTime ||
        !conditionalData.endDateTime ||
        !conditionalData.meetingDescription
      ) {
        toast.warn('All meeting fields are required');
        return;
      }
      Object.assign(payload, {
        assignees: conditionalData.assignees,
        time_zone: conditionalData.timeZone,
        start_date_time: conditionalData.startDateTime,
        end_date_time: conditionalData.endDateTime,
        description: conditionalData.meetingDescription,
      });
    }

    try {
      setStatusProcessing(true);
      const res = await updateLeadStatus({ leadId: lead.id, payload }).unwrap();
      if (res?.success) {
        toast.success('Status updated');
        handleCloseStatusDialog();
        refetchLead();
      }
    } catch (error) {
      toast.error(error?.message || 'Failed to update status');
    } finally {
      setStatusProcessing(false);
    }
  };


  const getStatusColor = (statusName) => {
    const colorMap = {
      'New': 'primary',
      'Contacted': 'info',
      'Qualified': 'success',
      'Connected': 'warning',
      'Lost': 'error',
      'Follow up': 'secondary',
      'Chatting': 'info',
      'Select': 'default',
      'Meeting': 'warning',
      'Upcoming': 'info'
    };
    return colorMap[statusName] || 'default';
  };

  const formatDateTime = (dateTimeString) => {
    if (!dateTimeString) return 'N/A';
    return new Date(dateTimeString).toLocaleString();
  };

  if (isLoading) {
    return (
      <PageLayout sx={{ justifyContent: 'center', alignItems: 'center' }}>
        <CircularProgress />
      </PageLayout>
    );
  }

  if (!lead) {
    return (
      <PageLayout sx={{ justifyContent: 'center', alignItems: 'center' }}>
        <Typography>Lead not found</Typography>
      </PageLayout>
    );
  }

  const meetingsList = Array.isArray(lead.meetings) ? lead.meetings : [];
  const safeMeetingTab = Math.min(meetingTab, Math.max(0, meetingsList.length - 1));
  const activeMeeting = meetingsList[safeMeetingTab];

  return (
    <PageLayout>
      <Container maxWidth="xl" sx={{ py: 4 }}>
          {/* Header with Breadcrumbs */}
          <Box sx={{ mb: 4 }}>
            <Breadcrumbs sx={{ mb: 2 }}>
              <Link
                color="inherit"
                onClick={handleBack}
                sx={{ cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
              >
                Leads
              </Link>
              <Typography color="text.primary">Lead Details</Typography>
            </Breadcrumbs>

            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <Box>
                <Typography variant="h4" sx={{ color: 'blue.600', fontWeight: 600, mb: 1 }}>
                  {lead.name}
                </Typography>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                  <Chip
                    label={lead.last_status_log?.status?.name}
                    color={getStatusColor(lead.last_status_log?.status?.name)}
                    size="small"
                  />
                  <Typography variant="body2" color="text.secondary">
                    Created: {formatDateTime(lead.created_at)}
                  </Typography>
                </Box>
              </Box>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  startIcon={<ArrowBackIcon />}
                  onClick={handleBack}
                  variant="outlined"
                >
                  Back to Leads
                </Button>
                <Can permission="lead-status">
                  <Button
                    variant="contained"
                    onClick={handleOpenStatusDialog}
                    disabled={statusProcessing}
                  >
                    Change Status
                  </Button>
                </Can>
              </Box>
            </Box>
          </Box>

          <Grid container spacing={3}>
            {/* Left Column - Lead Information */}
            <Grid item xs={12} md={8}>
              {/* Basic Information Card */}
              <Card sx={{ mb: 3 }}>
                <CardContent>
                  <Typography variant="h6" sx={{ mb: 2, color: 'blue.600', display: 'flex', alignItems: 'center', gap: 1 }}>
                    <PersonIcon />
                    Basic Information
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        Name
                      </Typography>
                      <Typography variant="body1" gutterBottom>
                        {lead.name || 'N/A'}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        Business Name
                      </Typography>
                      <Typography variant="body1" gutterBottom>
                        {lead.dba_name || 'N/A'}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        Email
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <EmailIcon fontSize="small" color="action" />
                        <Typography variant="body1">
                          {lead.email || 'N/A'}
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        Phone
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <PhoneIcon fontSize="small" color="action" />
                        <Typography variant="body1">
                          {lead.phone || 'N/A'}
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        Description
                      </Typography>
                      <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                        {lead.description || 'No description provided'}
                      </Typography>
                    </Grid>
                     <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        Country
                      </Typography>
                      <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                        {lead.country?.name || 'No country provided'}
                      </Typography>
                    </Grid>
                     <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        Created By
                      </Typography>
                      <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap', }}>
                        <Box sx={{display: 'flex', justifyContent: 'space-between' }}>
                       <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap', }}> {lead.author?.name || 'No author provided'}</Typography>  
                         <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap', }}> 
                        {lead?.author?.email || 'not found'} 
                        </Typography>
                      </Box>
                      </Typography>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>

              {/* Status History Card */}
              <Card sx={{ mb: 3 }}>
                <CardContent>
                  <Typography variant="h6" sx={{ mb: 2, color: 'blue.600', display: 'flex', alignItems: 'center', gap: 1 }}>
                    <ScheduleIcon />
                    Current Status
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        Current Status
                      </Typography>
                      <Chip
                        label={lead.last_status_log?.status?.name}
                        color={getStatusColor(lead.last_status_log?.status?.name)}
                        size="medium"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        Status Description
                      </Typography>
                      <Typography variant="body1">
                        {lead.last_status_log?.description || 'No description'}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        Status Created
                      </Typography>
                      <Typography variant="body1">
                        {formatDateTime(lead.last_status_log?.created_at)}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        Created By
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Avatar sx={{ width: 24, height: 24, fontSize: '0.75rem' }}>
                          {lead.last_status_log?.author?.name?.charAt(0) || 'U'}
                        </Avatar>
                        <Typography variant="body1">
                          {lead.last_status_log?.author?.name || 'Unknown'}
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>

              {/* Meetings (multiple) — from lead.meetings */}
              {meetingsList.length > 0 && (
                <Card sx={{ mb: 3 }}>
                  <CardContent>
                    <Typography variant="h6" sx={{ mb: 2, color: 'blue.600', display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CalendarIcon />
                      Meetings ({meetingsList.length})
                    </Typography>
                    <Tabs
                      value={safeMeetingTab}
                      onChange={(_, v) => setMeetingTab(v)}
                      variant="scrollable"
                      scrollButtons="auto"
                      sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}
                    >
                      {meetingsList.map((m, i) => (
                        <Tab
                          key={m.id || i}
                          label={
                            m.start_date_time
                              ? `Meeting ${i + 1} · ${formatDateTime(m.start_date_time)}`
                              : `Meeting ${i + 1}`
                          }
                        />
                      ))}
                    </Tabs>
                    {activeMeeting && (
                      <Box role="tabpanel">
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mb: 2 }}>
                          {activeMeeting.id && (
                            <>
                              <Can permission="meeting-view">
                                <Button variant="outlined" size="small" onClick={() => navigate(`${MEETING_VIEW_PATH}/${activeMeeting.id}`)}>
                                  View
                                </Button>
                              </Can>
                              <Can permission="meeting-edit">
                                <Button variant="contained" size="small" onClick={() => navigate(`${MEETING_EDIT_PATH}/${activeMeeting.id}`)}>
                                  Edit
                                </Button>
                              </Can>
                            </>
                          )}
                        </Box>
                        <Grid container spacing={2}>
                          <Grid item xs={12} sm={6}>
                            <Typography variant="body2" color="text.secondary" gutterBottom>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <TimeIcon fontSize="small" />
                                Start
                              </Box>
                            </Typography>
                            <Typography variant="body1">{formatDateTime(activeMeeting.start_date_time)}</Typography>
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <Typography variant="body2" color="text.secondary" gutterBottom>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <TimeIcon fontSize="small" />
                                End
                              </Box>
                            </Typography>
                            <Typography variant="body1">{formatDateTime(activeMeeting.end_date_time)}</Typography>
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <Typography variant="body2" color="text.secondary" gutterBottom>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <LanguageIcon fontSize="small" />
                                Time zone
                              </Box>
                            </Typography>
                            <Typography variant="body1">{activeMeeting.time_zone || '—'}</Typography>
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <Typography variant="body2" color="text.secondary" gutterBottom>
                              Created
                            </Typography>
                            <Typography variant="body1">{formatDateTime(activeMeeting.created_at)}</Typography>
                          </Grid>
                          {activeMeeting.google_event_url && (
                            <Grid item xs={12}>
                              <Typography variant="body2" color="text.secondary" gutterBottom>
                                Google Meet
                              </Typography>
                              <Link href={activeMeeting.google_event_url} target="_blank" rel="noopener noreferrer">
                                {activeMeeting.google_event_url}
                              </Link>
                            </Grid>
                          )}
                          {(activeMeeting.google_sync_status || activeMeeting.google_error_message) && (
                            <Grid item xs={12}>
                              <Typography variant="body2" color="text.secondary" gutterBottom>
                                Google sync
                              </Typography>
                              <Chip
                                size="small"
                                label={activeMeeting.google_sync_status || '—'}
                                color={activeMeeting.google_sync_status === 'failed' ? 'warning' : 'default'}
                                sx={{ mr: 1 }}
                              />
                              {activeMeeting.google_error_message && (
                                <Typography variant="caption" color="text.secondary" display="block">
                                  {activeMeeting.google_error_message}
                                </Typography>
                              )}
                            </Grid>
                          )}
                          <Grid item xs={12}>
                            <Typography variant="body2" color="text.secondary" gutterBottom>
                              Description
                            </Typography>
                            <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                              {activeMeeting.description || '—'}
                            </Typography>
                          </Grid>
                        </Grid>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              )}

              {/* Legacy: single meeting from last status log (when lead.meetings is empty) */}
              {meetingsList.length === 0 && lead.last_status_log?.meeting && (
                <Card sx={{ mb: 3 }}>
                  <CardContent>
                    <Typography variant="h6" sx={{ mb: 2, color: 'blue.600', display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CalendarIcon />
                      Meeting Information
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <TimeIcon fontSize="small" />
                            Start Time
                          </Box>
                        </Typography>
                        <Typography variant="body1">
                          {formatDateTime(lead.last_status_log.meeting.start_date_time)}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <TimeIcon fontSize="small" />
                            End Time
                          </Box>
                        </Typography>
                        <Typography variant="body1">
                          {formatDateTime(lead.last_status_log.meeting.end_date_time)}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <LanguageIcon fontSize="small" />
                            Time Zone
                          </Box>
                        </Typography>
                        <Typography variant="body1">
                          {lead.last_status_log.meeting.time_zone}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          Meeting Status
                        </Typography>
                        <Chip
                          label={lead.last_status_log.status?.name}
                          color={getStatusColor(lead.last_status_log.status?.name)}
                          size="small"
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          Meeting Description
                        </Typography>
                        <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                          {lead.last_status_log.meeting.description || 'No meeting description'}
                        </Typography>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              )}

              {/* Attachments Card */}
              <Card>
                <CardContent>
                  <Typography
                    variant="h6"
                    sx={{
                      mb: 2,
                      color: 'blue.600',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                    }}
                  >
                    <AttachmentIcon />
                    Attachments ({lead.attachments?.length || 0})
                  </Typography>

                  {lead.attachments && lead.attachments.length > 0 ? (
                    <TableContainer component={Paper} variant="outlined">
                      <Table>
                        <TableHead>
                          <TableRow>
                            <TableCell sx={{ fontWeight: 600 }}>File Name</TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>Type</TableCell>
                            <TableCell sx={{ fontWeight: 600, textAlign: 'center' }}>Action</TableCell>
                          </TableRow>
                        </TableHead>

                        <TableBody>
                          {lead.attachments.map((file) => (
                            <TableRow
                              key={file.id}
                              hover={file.type?.startsWith('image/')}
                              sx={{
                                cursor: file.type?.startsWith('image/') ? 'pointer' : 'default',
                              }}
                              onClick={
                                file.type?.startsWith('image/')
                                  ? () => handleImageClick(file.path)
                                  : undefined
                              }
                            >
                              {/* File Name */}
                              <TableCell>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <DescriptionIcon color="action" />
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      fontWeight: 500,
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                      whiteSpace: 'nowrap',
                                      maxWidth: 200,
                                    }}
                                    title={file.title}
                                  >
                                    {file.title}
                                  </Typography>
                                </Box>
                              </TableCell>

                              {/* File Type */}
                              <TableCell>
                                <Chip
                                  label={file.type?.split('/')[1]?.toUpperCase() || 'FILE'}
                                  size="small"
                                  variant="outlined"
                                />
                              </TableCell>

                              {/* Action Buttons */}
                              <TableCell sx={{ textAlign: 'center' }}>
                                <Can permission="attachment-view">
                                  <IconButton
                                    size="small"
                                    href={file.path}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                  >
                                    <VisibilityIcon fontSize="small" />
                                  </IconButton>
                                </Can>
                                <Can permission="attachment-view">
                                  <IconButton
                                    size="small"
                                    disabled={downloadingAttachmentId != null}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleDownloadFile(file);
                                    }}
                                  >
                                    {downloadingAttachmentId === (file?.id ?? file?.path) ? (
                                      <CircularProgress size={18} />
                                    ) : (
                                      <DownloadIcon fontSize="small" />
                                    )}
                                  </IconButton>
                                </Can>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  ) : (
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ textAlign: 'center', py: 3 }}
                    >
                      No attachments found
                    </Typography>
                  )}
                </CardContent>
              </Card>

            </Grid>

            {/* Right Column - Side Information */}
            <Grid item xs={12} md={4}>
              {/* Source & Brand Card */}
              <Card sx={{ mb: 3 }}>
                <CardContent>
                  <Typography variant="h6" sx={{ mb: 2, color: 'blue.600', display: 'flex', alignItems: 'center', gap: 1 }}>
                    <SourceIcon />
                    Source & Brand
                  </Typography>
                  <List dense>
                    <ListItem>
                      <ListItemIcon>
                        <SourceIcon color="action" />
                      </ListItemIcon>
                      <ListItemText
                        primary="Source"
                        secondary={lead.source?.name || 'Not specified'}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <BusinessIcon color="action" />
                      </ListItemIcon>
                      <ListItemText
                        primary="Brand"
                        secondary={lead.brand?.name || 'Not specified'}
                      />
                    </ListItem>
                  </List>
                </CardContent>
              </Card>

              {/* Assignees Card */}
              <Card sx={{ mb: 3 }}>
                <CardContent>
                  <Typography variant="h6" sx={{ mb: 2, color: 'blue.600', display: 'flex', alignItems: 'center', gap: 1 }}>
                    <AssignmentIcon />
                    Assignee
                  </Typography>
                  {/* {lead?.last_status_log?.assignee && lead?.last_status_log?.assignee.length > 0 ? ( */}
                  <List dense>
                    {(lead?.assignees ?? []).map((assignee, index) => (
                    <ListItem key={assignee?.id ?? index}>
                      <ListItemIcon>
                        <Avatar sx={{ width: 32, height: 32, fontSize: '0.875rem' }}>
                          {assignee?.name?.charAt(0) || 'U'}
                        </Avatar>
                      </ListItemIcon>
                      <ListItemText
                        primary={assignee?.name ?? 'Unknown'}
                        secondary={
                          <Box>
                            <Typography variant="body2" color="text.secondary">
                              {assignee?.email ?? '—'}
                            </Typography>
                            {assignee?.phone && (
                              <Typography variant="body2" color="text.secondary">
                                {assignee.phone}
                              </Typography>
                            )}
                          </Box>
                        }
                      />
                    </ListItem>
                    ))}
                  </List>
                  {/* ) : (
                    <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                      No assignees
                    </Typography>
                  )} */}
                </CardContent>
              </Card>

              {/* Status Assignee Card */}
              {/* {lead.status?.assignee && (
                <Card sx={{ mb: 3 }}>
                  <CardContent>
                    <Typography variant="h6" sx={{ mb: 2, color: 'blue.600', display: 'flex', alignItems: 'center', gap: 1 }}>
                      <PersonIcon />
                      Status Assignee
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Avatar sx={{ width: 48, height: 48 }}>
                        {lead.status.assignee.name?.charAt(0) || 'U'}
                      </Avatar>
                      <Box>
                        <Typography variant="subtitle1" fontWeight={500}>
                          {lead.status.assignee.name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {lead.status.assignee.email}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {lead.status.assignee.phone}
                        </Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              )} */}

              {/* DBA Information Card */}
              <Card>
                <CardContent>
                  <Typography variant="h6" sx={{ mb: 2, color: 'blue.600', display: 'flex', alignItems: 'center', gap: 1 }}>
                    <BusinessIcon />
                    DBA Information
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                    {lead.dba ? 'DBA details available' : 'No DBA associated'}
                  </Typography>
                  {lead.dba && (
                    <Button variant="outlined" fullWidth
                      onClick={() => navigate(`/view-merchant?id=${lead?.dba?.id}`)}
                    >
                      View DBA Details
                    </Button>
                  )}
                </CardContent>
              </Card>
            </Grid>
          </Grid>
      </Container>

      {/* Image Preview Dialog */}
      <Dialog
        open={openImageDialog}
        onClose={() => setOpenImageDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Image Preview</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <img
              src={selectedImage}
              alt="Preview"
              style={{
                maxWidth: '100%',
                maxHeight: '70vh',
                objectFit: 'contain'
              }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenImageDialog(false)}>Close</Button>
          <Button
            variant="contained"
            onClick={() => handleDownloadFile(selectedImage, 'image.png')}
          >
            Download
          </Button>
        </DialogActions>
      </Dialog>

      <StatusDialog
        open={statusDialogOpen}
        selectedLead={lead}
        newStatus={newStatus}
        conditionalData={conditionalData}
        statusOptions={statusOptions}
        frontSellerOptions={frontSellerOptions}
        processingId={statusProcessing ? lead?.id : null}
        onClose={handleCloseStatusDialog}
        onStatusChange={handleStatusChange}
        onNewStatusChange={setNewStatus}
        onConditionalChange={handleConditionalChange}
      />
    </PageLayout>
  );
};

export default LeadDetail;